import {
	type MergedGpEventResponse,
	type GameClipIntervalRequest,
	type EventListTeamInfo,
	type GameEventCommentProps,
	type GPGamePlayerResponse,
} from "../../../types";
import type { ReportEventErrorHandler } from "../../../report-error";
import "./eventList.css";
import { _ } from "i18n";
import { useGameStore } from "../../../store";
import { useEffect } from "react";
import { useTimeStore } from "../../../time-store";
import { handlePlayEvent } from "../../../functions";
import { EventListContainer } from "./EventListContainer";
import { useVideoPlayer } from "../../../Contexts/contexts";

interface EventListProps {
	handleCreateClip?: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	isClip?: boolean;
	clipsPermission?: boolean;
	eventsPermission?: boolean;
	reportEventErrorPermission?: boolean;
	onReportError?: ReportEventErrorHandler;
	eventCommentsPermission?: boolean;
	teamInfo: EventListTeamInfo;
	mergedEvents?: MergedGpEventResponse[];
	gameEventComment?: GameEventCommentProps;
	players?: GPGamePlayerResponse[];
}

export function EventList({
	clipsPermission,
	eventsPermission,
	reportEventErrorPermission,
	onReportError,
	handleCreateClip,
	isClip,
	teamInfo,
	eventCommentsPermission,
	gameEventComment,
	players,
}: EventListProps) {
	const { canPlay, playerRef, isPlaying } = useVideoPlayer();
	const { mergedEvents, events, jumpBetweenIntervals, intervals } = useGameStore();
	const { currentTime, setCurrentTime } = useTimeStore();
	useEffect(() => {
		if (!isPlaying || !jumpBetweenIntervals || intervals.length === 0) {
			return;
		}

		const currentInterval = intervals.find((i) => currentTime >= i.start && currentTime <= i.end);

		if (currentInterval === undefined) {
			let nextInterval = null;
			const nextIntervals = intervals.filter((i) => i.start >= currentTime).sort((a, b) => a.start - b.start);

			if (nextIntervals.length !== 0) {
				nextInterval = nextIntervals[0];
			}

			if (!nextInterval && intervals.length !== 0) {
				nextInterval = intervals[0];
			}
			if (nextInterval && canPlay) {
				setCurrentTime(nextInterval.start);
				handlePlayEvent(playerRef, nextInterval.start);
			}
		}
	}, [currentTime, intervals, canPlay, isPlaying, jumpBetweenIntervals, setCurrentTime, playerRef]);

	return (
		<EventListContainer
			currentTime={currentTime}
			clipsPermission={clipsPermission}
			eventsPermission={eventsPermission}
			reportEventErrorPermission={reportEventErrorPermission}
			onReportError={onReportError}
			handleCreateClip={handleCreateClip}
			playerRef={playerRef}
			isClip={isClip}
			teamInfo={teamInfo}
			eventCount={events.length}
			mergedEvents={mergedEvents}
			eventCommentsPermission={eventCommentsPermission || false}
			gameEventComment={gameEventComment}
			players={players}
		/>
	);
}
